import { useOrderPanelStore } from '../../stores/order-panel-store'
import { calculateArea, calculateVloerkleedTotalPrice } from '../../lib/pricing/vloerkleed'
import { formatEuros } from '../../lib/pricing/money'
import { findOffertePrimaryProduct } from '../../lib/offerte-products'

export function PriceSummary() {
  const items = useOrderPanelStore((s) => s.items)
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const selectedProductSku = useOrderPanelStore((s) => s.selectedProductSku)
  const discountPercentage = useOrderPanelStore((s) => s.discountPercentage)
  const discountReason = useOrderPanelStore((s) => s.discountReason)

  if (!bootstrap) return null

  const totals = calculateVloerkleedTotalPrice(items, bootstrap, selectedProductSku)

  // No valid items
  if (totals.items.length === 0) {
    return (
      <div className="px-4 py-3 bg-slate-50 rounded-lg text-sm text-slate-500 text-center">
        Vul afmetingen in om de prijs te berekenen
      </div>
    )
  }

  // Apply manual discount
  const manualDiscountCents = discountPercentage > 0
    ? Math.round(totals.subtotalCents * (discountPercentage / 100))
    : 0
  const afterManualDiscountCents = totals.subtotalCents - manualDiscountCents
  const btwCents = Math.round(afterManualDiscountCents * 0.21)
  const finalTotalCents = afterManualDiscountCents + btwCents

  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
      <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide">Prijsoverzicht</h4>

      {/* Per item */}
      {totals.items.map((result, idx) => {
        const item = items.find(i => i.id === result.itemId)
        const product = findOffertePrimaryProduct(item?.productCode)
        let label: string
        if (product.mode === 'count') {
          label = `${product.label} × ${item?.aantal ?? 0}`
        } else {
          const { areaM2Rounded } = item ? calculateArea(item) : { areaM2Rounded: 0 }
          label = `${product.label} ${idx + 1} (${areaM2Rounded}m²)`
        }
        return (
          <div key={result.itemId} className="flex justify-between items-start text-sm">
            <span className="text-slate-600">{label}</span>
            <span className="text-right">
              <span className="text-slate-700 block">{formatEuros(result.totalCents)}</span>
              {/* Telefoonscript: de klant hoort de incl-prijs per kleed.
                  Item-prijs is excl. order-brede (handmatige) korting. */}
              <span className="text-xs text-slate-400 block">
                incl. BTW {formatEuros(Math.round(result.totalCents * 1.21))}
              </span>
            </span>
          </div>
        )
      })}

      {/* Subtotal */}
      <div className="flex justify-between text-sm pt-1 border-t border-slate-200">
        <span className="text-slate-600">Subtotaal (excl. BTW)</span>
        <span className="text-slate-700">{formatEuros(totals.subtotalCents)}</span>
      </div>

      {/* Bundle discount */}
      {totals.discountCents > 0 && totals.discountLabel && (
        <div className="flex justify-between text-sm text-emerald-600">
          <span>{totals.discountLabel}</span>
          <span>-{formatEuros(totals.discountCents)}</span>
        </div>
      )}

      {/* Manual discount */}
      {manualDiscountCents > 0 && (
        <div className="flex justify-between text-sm text-orange-600">
          <span>Korting {discountPercentage}% — {discountReason || 'geen reden'}</span>
          <span>-{formatEuros(manualDiscountCents)}</span>
        </div>
      )}

      {/* BTW */}
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">BTW (21%)</span>
        <span className="text-slate-700">{formatEuros(btwCents)}</span>
      </div>

      {/* Total */}
      <div className="flex justify-between text-base font-semibold pt-1 border-t border-slate-200">
        <span className="text-slate-900">Totaal</span>
        <span className={discountPercentage === 100 ? 'text-emerald-600' : 'text-slate-900'}>
          {discountPercentage === 100 ? 'Gratis' : formatEuros(finalTotalCents)}
        </span>
      </div>

      {/* Minimum order warning */}
      {!totals.minimumOrderMet && totals.minimumOrderAmountCents > 0 && (
        <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700">
          Minimumorder {formatEuros(totals.minimumOrderAmountCents)} excl. BTW. Prijs is opgehoogd naar minimum.
        </div>
      )}
    </div>
  )
}
